import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import PrintIcon from '@material-ui/icons/Print';
import CloseIcon from '@material-ui/icons/Close';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import RefreshIcon from '@material-ui/icons/Refresh';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import MusicNoteIcon from '@material-ui/icons/MusicNote';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HistoryIcon from '@material-ui/icons/History';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import useApi, { callApi } from '../../utils/useApi';
import StoreContext from '../../components/Store/context';
import { marked } from 'marked';
import { POPULAR_UNIT_CODES, formatUnitDisplay } from '../../constants/productUnits';
import './Chatbot.css';

marked.setOptions({ breaks: true, gfm: true });

const WELCOME_MESSAGE = {
  role: 'assistant',
  content:
    'Olá! 👋 Sou a **duxIA**, sua assistente executiva do sistema Duzz. Como posso te ajudar hoje?',
};

const SPECIALISTS = [
  {
    key: 'general',
    title: 'duxIA Geral',
    shortTitle: 'Geral',
    subtitle: 'Consultora Executiva & Visão Geral',
    icon: (
      <img
        src="/image/duxia.jpg"
        alt="duxIA"
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    ),
    defaultWelcome:
      'Olá! 👋 Sou a **duxIA Geral**. Pergunte qualquer dúvida sobre vendas, clientes ou solicite tarefas gerais.',
  },
  {
    key: 'commercial',
    title: 'duxIA Operação',
    shortTitle: 'Operação',
    subtitle: 'Comandas, Vendas & Atendimento',
    icon: '⚡',
    defaultWelcome:
      'Olá! 👋 Sou a **duxIA Operação**. Acompanho comandas abertas, fechamento de vendas para baixa de estoque e oportunidades comerciais.',
  },
  {
    key: 'financial',
    title: 'duxIA Financeiro',
    shortTitle: 'Finanças',
    subtitle: 'Contas a Pagar, Caixas & Despesas',
    icon: '💰',
    defaultWelcome:
      'Olá! 👋 Sou a **duxIA Financeiro**. Posso te ajudar a pagar contas, lançar despesas, registrar retiradas e consultar fluxo de caixa.',
  },
  {
    key: 'stock',
    title: 'duxIA Estoque',
    shortTitle: 'Estoque',
    subtitle: 'Curva ABC, Rupturas & Reposição',
    icon: '📦',
    defaultWelcome:
      'Olá! 👋 Sou a **duxIA Estoque**. Monitore rupturas, produtos sem giro e receba sugestões inteligentes de reposição.',
  },
];

const SPECIALIST_SUGGESTIONS = {
  general: [
    { label: '📊 Resumo do dia', prompt: 'Qual o total de vendas realizadas hoje?' },
    { label: '👥 Melhores clientes', prompt: 'Mostre os clientes que mais compraram este mês' },
    { label: '🔥 Mais vendidos', prompt: 'Quais são os produtos mais vendidos recentemente?' },
  ],
  commercial: [
    { label: '📋 Comandas antigas', prompt: 'Quais comandas estão abertas há mais de 14 dias (2 semanas)?' },
    { label: '👥 Clientes sumidos', prompt: 'Quais clientes habituais não compram há mais de 30 dias?' },
    { label: '🚀 Combos & Promoções', prompt: 'Sugira um combo promocional para aumentar o ticket médio' },
  ],
  financial: [
    { label: '💰 Registrar despesa', prompt: 'Paguei uma despesa avulsa hoje' },
    { label: '📑 Contas a pagar', prompt: 'Quais contas temos a pagar vencendo nos próximos dias?' },
    { label: '💵 Retirada / Pró-labore', prompt: 'Quero registrar uma retirada de pró-labore' },
    { label: '🏦 Saldo dos caixas', prompt: 'Qual o saldo atual dos caixas abertos?' },
  ],
  stock: [
    { label: '⚠️ Estoque baixo', prompt: 'Quais produtos com giro estão em ruptura ou com estoque crítico pela Curva ABC?' },
    { label: '📈 Curva ABC', prompt: 'Mostre os produtos mais importantes da Curva A de estoque' },
    { label: '📦 Sugerir reposição', prompt: 'Quais itens precisam de reposição de estoque?' },
  ],
};

const PARAM_LABELS = {
  // Identificação e Cadastro
  id: 'ID do Item',
  productId: 'ID do Produto',
  name: 'Nome / Descrição',
  description: 'Descrição',
  value: 'Valor',
  price: 'Preço de Venda',
  cost: 'Custo Unitário',
  amount: 'Quantidade',
  quantity: 'Quantidade',
  unit: 'Unidade de Medida',
  field: 'Campo Alterado',
  category: 'Categoria',
  categoryId: 'Categoria',
  supplier: 'Fornecedor',
  supplierId: 'Fornecedor',
  barCode: 'Código de Barras',
  particulars: 'Particularidades',
  inputs: 'Insumos / Componentes',
  show_in_catalog: 'Exibir no Catálogo',
  isPublic: 'Público no Catálogo',
  assign_item_id: 'Vincular ao Item ID',
  assign_item_type: 'Tipo de Vínculo',

  // Financeiro, Caixa e Despesas
  cashRegister: 'Caixa',
  sourceCashRegister: 'Caixa de Origem',
  targetCashRegister: 'Caixa de Destino',
  sender: 'Caixa de Origem',
  receiver: 'Caixa de Destino',
  paymentMethod: 'Forma de Pagamento',
  dueDay: 'Dia do Vencimento',
  isRecurrent: 'Recorrente',
  frequency: 'Frequência',
  date: 'Data',
  reason: 'Motivo',
  justification: 'Justificativa',
  billId: 'Conta a Pagar',
  ids: 'Contas a Pagar',
  costId: 'Custo',
  valuePaid: 'Valor Pago',
  operation: 'Operação',

  // Promoções, Combos e Cupons
  discount: 'Desconto',
  percentage: 'Desconto (%)',
  couponCode: 'Código do Cupom',
  products: 'Produtos Participantes',
  customerProfile: 'Perfil de Clientes Alvo',

  // Clientes, Fornecedores e Contatos
  customer: 'Cliente',
  customerId: 'ID do Cliente',
  whatsapp: 'WhatsApp',
  phone: 'Telefone',
  email: 'E-mail',
  document: 'CPF / CNPJ',
  cnpj: 'CNPJ',
  city: 'Cidade',
  cities: 'Cidades',
  neighborhood: 'Bairro',
  neighborhoods: 'Bairros',
  street: 'Rua',
  streets: 'Ruas',
  zipCode: 'CEP',
  genders: 'Gênero(s)',
  marialStatus: 'Estado Civil',
  ageRange: 'Faixa Etária',
  childrenRange: 'Faixa de Filhos',

  // Segmentação de Clientes / Perfis de Retenção
  profileType: 'Tipo de Perfil',
  recencyMin: 'Inatividade Mínima',
  recencyRange: 'Faixa de Inatividade',
  totalSalesValueMin: 'Valor Mínimo de Compras',
  totalSalesValueRange: 'Faixa de Compras',
  ticketAvgMin: 'Ticket Médio Mínimo',
  ticketAvgRange: 'Faixa de Ticket Médio',
  unpaidValueMin: 'Dívida / Fiado Mínimo',
  unpaidValueRange: 'Faixa de Dívida / Fiado',
  pendingSalesCountMin: 'Mín. Fiados Pendentes',
  pendingSalesCountRange: 'Faixa de Fiados Pendentes',
  totalDiscountsMin: 'Descontos Mínimos Concedidos',
  totalDiscountsRange: 'Faixa de Descontos',
  tenureMin: 'Tempo Mínimo de Casa',
  tenureRange: 'Faixa de Tempo de Casa',
  payIntervalMin: 'Intervalo Mín. de Pagamento',
  payIntervalRange: 'Faixa de Intervalo de Pagamento',

  // Comandas e Vendas
  session_id: 'Comanda Nº',
  identifier: 'Identificador',
  total: 'Total',
  acrescimo: 'Acréscimo',
  surcharge: 'Acréscimo',

  // Lotes e Operações
  operations: 'Operações em Lote',
};

const getParamLabel = (key) => {
  if (PARAM_LABELS[key]) return PARAM_LABELS[key];
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase());
};

const PAYMENT_METHOD_NAMES = {
  1: 'Cartão de crédito',
  2: 'Cartão de débito',
  3: 'PIX',
  4: 'Dinheiro',
  5: 'Sangria / Transf. Interna',
  6: 'Boleto',
  7: 'Carnê',
  8: 'Vale',
  9: 'Desconto',
};

const formatParamValue = (key, val) => {
  if (val === null || val === undefined || val === '') return '-';
  if (key === 'unit') {
    return formatUnitDisplay(val, true);
  }
  if (key === 'paymentMethod') {
    return PAYMENT_METHOD_NAMES[val] || `Método #${val}`;
  }
  if (
    key === 'cashRegister' ||
    key === 'sender' ||
    key === 'receiver' ||
    key === 'sourceCashRegister' ||
    key === 'targetCashRegister'
  ) {
    return `Caixa #${val}`;
  }
  if (
    key === 'value' ||
    key === 'price' ||
    key === 'cost' ||
    key === 'total' ||
    key === 'valuePaid' ||
    key === 'totalSalesValueMin' ||
    key === 'ticketAvgMin' ||
    key === 'unpaidValueMin' ||
    key === 'totalDiscountsMin'
  ) {
    const num = Number(val);
    if (!isNaN(num)) {
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
  }
  if (key === 'profileType') {
    return Number(val) === 2 ? 'Perfil de Risco (Inativo / Devedor)' : 'Perfil Saudável / Promissor';
  }
  if (key === 'recencyMin') {
    return `${val} dias sem comprar`;
  }
  if (key === 'tenureMin') {
    return `${val} dias de cadastro`;
  }
  if (key === 'payIntervalMin') {
    return `${val} dias de intervalo médio`;
  }
  if (key === 'pendingSalesCountMin') {
    return `${val} venda(s) pendente(s)`;
  }
  if (key.endsWith('Range') && typeof val === 'string') {
    const parts = val.includes('~') ? val.split('~') : (val.includes(',') ? val.split(',') : [val]);
    if (parts.length === 2) {
      if (
        key.includes('totalSalesValue') ||
        key.includes('ticketAvg') ||
        key.includes('unpaidValue') ||
        key.includes('totalDiscounts')
      ) {
        const n1 = Number(parts[0]);
        const n2 = Number(parts[1]);
        if (!isNaN(n1) && !isNaN(n2)) {
          return `${n1.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} até ${n2.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
        }
      }
      if (key.includes('recency') || key.includes('tenure') || key.includes('payInterval')) {
        return `${parts[0]} a ${parts[1]} dias`;
      }
      if (key.includes('age')) {
        return `${parts[0]} a ${parts[1]} anos`;
      }
      return `${parts[0]} a ${parts[1]}`;
    }
  }
  if (key === 'dueDay') {
    return `Dia ${val}`;
  }
  if (key === 'percentage' || key === 'discount') {
    const num = Number(val);
    if (!isNaN(num)) return `${num}%`;
  }
  if (key === 'frequency') {
    if (val === 'monthly' || val === 'mensal') return 'Mensal';
    if (val === 'annual' || val === 'yearly' || val === 'anual') return 'Anual';
    if (val === 'weekly' || val === 'semanal') return 'Semanal';
    if (val === 'daily' || val === 'diario') return 'Diário';
    return String(val);
  }
  if (key === 'isRecurrent') {
    return val ? 'Sim (Recorrente)' : 'Não';
  }
  if (typeof val === 'boolean') return val ? 'Sim' : 'Não';
  if (Array.isArray(val)) {
    return val.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
  }
  return String(val);
};

const formatMessage = (text) => {
  if (!text) return '';
  return marked.parse(text);
};

const parseDate = (dateVal) => {
  if (!dateVal) return null;
  if (typeof dateVal === 'string') {
    const isoDate = new Date(dateVal);
    if (!isNaN(isoDate.getTime())) return isoDate;
    if (dateVal.includes('/')) {
      const [datePart, timePart] = dateVal.split(' ');
      const [day, month, year] = datePart.split('/');
      const [hour = '00', minute = '00'] = (timePart || '').split(':');
      const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
      if (!isNaN(parsed.getTime())) return parsed;
    }
  }
  const fallback = new Date(dateVal);
  return isNaN(fallback.getTime()) ? null : fallback;
};

const formatMessageTime = (isoOrDate) => {
  const d = parseDate(isoOrDate);
  if (!d) {
    return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  const timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return timeStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month} ${timeStr}`;
};

export const SOUND_PRESETS = [
  { id: 'chime', name: '🔔 Chime com Acorde Triplo (Padrão duxIA)', desc: 'Chime harmônico com cascata tripla cristalina' },
  { id: 'pop', name: '🫧 Pop Gota Suave', desc: 'Curto, discreto e moderno estilo iOS' },
  { id: 'ding', name: '🛎️ Ding Sino Cristalino', desc: 'Tom agudo e limpo de alta clareza' },
  { id: 'digital', name: '⚡ Digital Tech', desc: 'Rápido, futurista e tecnológico' },
  { id: 'marimba', name: '🎵 Acorde Triplo Clássico', desc: 'Harmonia suave de 3 notas musicais' },
];

export const playSoundById = (soundId) => {
  try {
    const isMuted = localStorage.getItem('chatbot_sound_muted') === 'true';
    if (isMuted) return;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    if (soundId === 'pop') {
      // Pop / Gota suave
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(350, now);
      osc.frequency.exponentialRampToValueAtTime(1100, now + 0.07);
      gain.gain.setValueAtTime(0.14, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.1);
    } else if (soundId === 'ding') {
      // Ding cristalino agudo
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1174.66, now); // D6
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.4);
    } else if (soundId === 'digital') {
      // Digital Tech
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(659.25, now);
      osc.frequency.setValueAtTime(987.77, now + 0.05);
      gain.gain.setValueAtTime(0.09, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.16);
    } else if (soundId === 'marimba') {
      // Acorde Triplo Dó - Mi - Sol
      const freqs = [523.25, 659.25, 783.99];
      freqs.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + idx * 0.04);
        gain.gain.setValueAtTime(0.08, now + idx * 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.04 + 0.18);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + idx * 0.04);
        osc.stop(now + idx * 0.04 + 0.2);
      });
    } else {
      // Combinação Chime Harmônico + Acorde Triplo (Padrão duxIA)
      // Ré5 (587.33Hz) -> Fá#5 (739.99Hz) -> Lá5 (880Hz) -> Ré6 (1174.66Hz)
      const notes = [
        { freq: 587.33, start: 0.0, gain: 0.10, duration: 0.28 },
        { freq: 739.99, start: 0.05, gain: 0.08, duration: 0.26 },
        { freq: 880.00, start: 0.10, gain: 0.11, duration: 0.32 },
        { freq: 1174.66, start: 0.15, gain: 0.08, duration: 0.35 },
      ];

      notes.forEach((note) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(note.freq, now + note.start);
        gain.gain.setValueAtTime(note.gain, now + note.start);
        gain.gain.exponentialRampToValueAtTime(0.001, now + note.start + note.duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + note.start);
        osc.stop(now + note.start + note.duration + 0.02);
      });
    }
  } catch {
    // Silent fail
  }
};

const playNotificationSound = () => {
  const currentSound = localStorage.getItem('chatbot_sound_preset') || 'chime';
  playSoundById(currentSound);
};

const CollapsibleDataTable = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!data) return null;
  const items = Array.isArray(data) ? data : [data];
  if (items.length === 0 || typeof items[0] !== 'object' || items[0] === null) return null;
  const headers = Object.keys(items[0]);
  if (headers.length === 0) return null;
  const rowCount = items.length;
  const colCount = headers.length;

  return (
    <div className="chatbot-collapsible">
      <button
        className={`chatbot-collapsible__toggle ${isExpanded ? 'chatbot-collapsible__toggle--open' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="chatbot-collapsible__label">
          📊 {rowCount} {rowCount === 1 ? 'registro' : 'registros'} · {colCount} colunas
        </span>
        <ExpandMoreIcon
          className={`chatbot-collapsible__icon ${isExpanded ? 'chatbot-collapsible__icon--open' : ''}`}
          style={{ fontSize: 18 }}
        />
      </button>
      <div className={`chatbot-collapsible__body ${isExpanded ? 'chatbot-collapsible__body--open' : ''}`}>
        <div className="chatbot-data-table-container">
          <table className="chatbot-data-table">
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row, i) => (
                <tr key={i}>
                  {headers.map((h) => (
                    <td key={h}>{formatParamValue(h, row[h])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ActionProposalCard = ({ action, onExecute, onCancel, isExecuting, result }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(action?.params?.unit || 'UN');

  useEffect(() => {
    setSelectedUnit(action?.params?.unit || 'UN');
  }, [action?.params?.unit]);

  if (!action) return null;
  const { title, summary, params, type } = action;

  // Tratamento dedicado para Ação de Copiar Lista de Compras Completa
  if (type === 'copy_shopping_list') {
    const handleCopyList = () => {
      const textToCopy = params?.copyText || summary || '';
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(textToCopy);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = textToCopy;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3500);
      } catch {
        // fallback
      }
    };

    return (
      <div className="chatbot-action-card chatbot-action-card--shopping-list">
        <div className="chatbot-action-card__header">
          <div className="chatbot-action-card__badge chatbot-action-card__badge--shopping">
            <span>📋</span>
            <span>Lista de Compras</span>
          </div>
          <span className="chatbot-action-card__type">{title || 'Pedido de Reposição'}</span>
        </div>

        {summary && <p className="chatbot-action-card__summary">{summary}</p>}

        {showPreview && params?.copyText && (
          <div className="chatbot-shopping-preview">
            <pre>{params.copyText}</pre>
          </div>
        )}

        <div className="chatbot-action-card__footer">
          <div className="chatbot-action-card__buttons">
            <button
              className={`chatbot-action-card__btn ${isCopied ? 'chatbot-action-card__btn--copied' : 'chatbot-action-card__btn--confirm'
                }`}
              onClick={handleCopyList}
            >
              {isCopied ? '✅ Lista Copiada com Sucesso!' : '📋 Copiar Lista de Compras Completa'}
            </button>
            {params?.copyText && (
              <button
                className="chatbot-action-card__btn chatbot-action-card__btn--cancel"
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? 'Ocultar' : 'Visualizar'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isExecuted = Boolean(action.executed || (result && result.success));
  const isCancelled = Boolean(action.cancelled || (result && result.cancelled));
  const successMessage =
    (result && result.message) ||
    (action.result && action.result.message) ||
    'Operação realizada com sucesso no sistema!';

  const executedBy = action.executedBy || (result && result.executedBy);
  const cancelledBy = action.cancelledBy || (result && result.cancelledBy);

  if (isCancelled) {
    return (
      <div className="chatbot-action-card chatbot-action-card--cancelled">
        <div className="chatbot-action-card__status-line">
          <span>❌ Recusado</span>
          {cancelledBy?.name && (
            <span className="chatbot-action-card__signer">
              por <strong>{cancelledBy.name}</strong>
              {cancelledBy.timestamp && ` em ${formatMessageTime(cancelledBy.timestamp)}`}
            </span>
          )}
        </div>
      </div>
    );
  }

  if (isExecuted) {
    return (
      <div className="chatbot-action-card chatbot-action-card--executed">
        <div className="chatbot-action-card__header">
          <div className="chatbot-action-card__badge chatbot-action-card__badge--success">
            <CheckCircleIcon style={{ fontSize: 15 }} />
            <span>Ação Executada</span>
          </div>
          <span className="chatbot-action-card__type">{title || 'Ação Concluída'}</span>
        </div>
        <div className="chatbot-action-card__success-msg">{successMessage}</div>
        {executedBy?.name && (
          <div className="chatbot-action-card__signer-footer">
            <span>
              Autorizado por <strong>{executedBy.name}</strong>
              {executedBy.timestamp && ` em ${formatMessageTime(executedBy.timestamp)}`}
            </span>
          </div>
        )}
      </div>
    );
  }

  const isCritical = Boolean(action?.critical);

  const actionToExecute =
    type === 'create_product' || type === 'edit_product'
      ? { ...action, params: { ...(action.params || {}), unit: selectedUnit } }
      : action;

  const handleApproveClick = () => {
    if (isCritical) {
      setConfirmOpen(true);
    } else {
      onExecute(actionToExecute);
    }
  };

  const handleConfirmCritical = () => {
    setConfirmOpen(false);
    onExecute(actionToExecute);
  };

  const confirmation = action?.confirmation || {};
  const confirmTitle = confirmation.title || 'Confirmação de Ação';
  const confirmMessage =
    confirmation.message ||
    action.description ||
    summary ||
    'Deseja realmente aprovar e executar esta operação?';
  const confirmWarning = confirmation.warning;
  const confirmDetails = Array.isArray(confirmation.details) ? confirmation.details : null;
  const confirmBtnText = confirmation.confirmButton || 'Sim, Confirmar e Executar';
  const cancelBtnText = confirmation.cancelButton || 'Cancelar';

  return (
    <div className="chatbot-action-card">
      <div className="chatbot-action-card__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div className="chatbot-action-card__badge">
            <span>✨</span>
            <span>Ação Sugerida</span>
          </div>
          {isCritical && (
            <div className="chatbot-action-card__badge chatbot-action-card__badge--critical">
              <span>⚠️</span>
              <span>Requer Confirmação</span>
            </div>
          )}
        </div>
        <span className="chatbot-action-card__type">{title || 'Confirmação'}</span>
      </div>

      {summary && <p className="chatbot-action-card__summary">{summary}</p>}

      {params && Object.keys(params).length > 0 && (
        <div className="chatbot-action-card__params">
          {Object.entries(params)
            .filter(([k, v]) => (type === 'create_product' || type === 'edit_product' ? k !== 'unit' : true) && v !== '' && v !== null && v !== undefined)
            .map(([k, v]) => (
              <div key={k} className="chatbot-action-card__param-row">
                <span className="chatbot-action-card__param-key">{getParamLabel(k)}:</span>
                <span className="chatbot-action-card__param-value">{formatParamValue(k, v)}</span>
              </div>
            ))}
        </div>
      )}

      {(type === 'create_product' || type === 'edit_product') && (
        <div
          className="chatbot-action-card__unit-selector"
          style={{
            marginTop: 8,
            marginBottom: 8,
            padding: '8px 10px',
            background: '#f8fafc',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#475569',
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span>Unidade de Medida:</span>
            <span style={{ color: '#2563eb', fontWeight: 700 }}>
              {formatUnitDisplay(selectedUnit)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {POPULAR_UNIT_CODES.map((u) => {
              const isSelected = selectedUnit === u;
              return (
                <button
                  key={u}
                  type="button"
                  onClick={() => setSelectedUnit(u)}
                  style={{
                    padding: '3px 8px',
                    fontSize: 11,
                    fontWeight: isSelected ? 700 : 500,
                    borderRadius: 12,
                    border: isSelected ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                    background: isSelected ? '#eff6ff' : '#ffffff',
                    color: isSelected ? '#1d4ed8' : '#64748b',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {u}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {result && result.error && (
        <div className="chatbot-action-card__error">⚠️ {result.error}</div>
      )}

      <div className="chatbot-action-card__footer">
        <div className="chatbot-action-card__buttons">
          <button
            className={`chatbot-action-card__btn ${
              isCritical ? 'chatbot-action-card__btn--critical' : 'chatbot-action-card__btn--confirm'
            }`}
            onClick={handleApproveClick}
            disabled={isExecuting}
          >
            {isExecuting ? (
              <>
                <CircularProgress size={13} style={{ color: '#fff', marginRight: 6 }} />
                Executando...
              </>
            ) : (
              'Aprovar e Executar'
            )}
          </button>
          <button
            className="chatbot-action-card__btn chatbot-action-card__btn--cancel"
            onClick={() => (onCancel ? onCancel(action) : onExecute(null))}
            disabled={isExecuting}
          >
            Recusar
          </button>
        </div>
      </div>

      {/* Modal de Confirmação Crítica isolado no próprio card */}
      {isCritical && (
        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: 14,
              padding: '4px',
              border: '1px solid #fed7aa',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <DialogTitle
            style={{
              padding: '16px 20px 6px',
              fontSize: 16,
              fontWeight: 700,
              color: '#9a3412',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ fontSize: 20 }}>⚠️</span>
            {confirmTitle}
          </DialogTitle>
          <DialogContent style={{ padding: '8px 20px 16px' }}>
            <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#374151', lineHeight: 1.5 }}>
              {confirmMessage}
            </p>

            {confirmDetails && confirmDetails.length > 0 && (
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '10px 12px',
                  marginBottom: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {confirmDetails.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      fontSize: 12.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 8,
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#64748b', minWidth: 90 }}>
                      {item.label}:
                    </span>
                    <span style={{ fontWeight: 600, color: '#1e293b', textAlign: 'right' }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {confirmWarning && (
              <div
                style={{
                  background: '#fffbeb',
                  border: '1px solid #fef3c7',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontSize: 12,
                  color: '#92400e',
                  lineHeight: 1.4,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 14, flexShrink: 0 }}>ℹ️</span>
                <span>{confirmWarning}</span>
              </div>
            )}
          </DialogContent>
          <DialogActions style={{ padding: '8px 20px 14px', gap: 8 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              style={{
                fontSize: 12.5,
                textTransform: 'none',
                color: '#64748b',
                fontWeight: 600,
              }}
            >
              {cancelBtnText}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmCritical}
              style={{
                background: '#ea580c',
                color: '#fff',
                fontSize: 12.5,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 8,
                boxShadow: 'none',
              }}
            >
              {confirmBtnText}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

const Chatbot = () => {
  const {
    credencials,
    chatbotOpen,
    setChatbotOpen,
    chatbotSpecialist,
    setChatbotSpecialist,
    chatbotInitialPrompt,
    setChatbotInitialPrompt,
    chatbotUnreadCount,
    setChatbotUnreadCount,
  } = useContext(StoreContext);

  const isOpen = chatbotOpen;
  const setIsOpen = setChatbotOpen;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeSpecialist, setActiveSpecialist] = useState('general');
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [actionResults, setActionResults] = useState({});
  const [executingIdx, setExecutingIdx] = useState(null);
  const [isMuted, setIsMuted] = useState(() => localStorage.getItem('chatbot_sound_muted') === 'true');
  const [soundPreset, setSoundPreset] = useState(() => localStorage.getItem('chatbot_sound_preset') || 'chime');
  const [soundModalOpen, setSoundModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  // Paginação com Infinite Scroll Reverso (lotes de 5 mensagens ao rolar para cima)
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 5,
    hasMore: false,
    isLoadingMore: false,
  });

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isAutoScrollingRef = useRef(false);
  const inputRef = useRef(null);

  const [sendMessage] = useApi({ method: 'post', url: '/chatbot' });
  const [fetchSession] = useApi({ method: 'get' });
  const [deleteSessionApi] = useApi({ method: 'delete' });
  const [markReadApi] = useApi({ method: 'post', url: '/chatbot/read' });
  const [executeActionApi] = useApi({ method: 'post', url: '/duxia/actions/execute' });
  const [cancelActionApi] = useApi({ method: 'post', url: '/duxia/actions/cancel' });

  const getSessionSpecialist = (s) => {
    if (s?.specialist && s.specialist !== 'general') return s.specialist;
    if (s?.state?.specialist && s.state.specialist !== 'general') return s.state.specialist;
    const title = (s?.title || '').toLowerCase();
    if (
      title.includes('operação') ||
      title.includes('operacao') ||
      title.includes('comercial') ||
      title.includes('comanda')
    ) {
      return 'commercial';
    }
    if (title.includes('estoque')) return 'stock';
    if (title.includes('financeiro') || title.includes('finança') || title.includes('financa')) return 'financial';
    return s?.specialist || s?.state?.specialist || 'general';
  };

  const hasAccess = Boolean(credencials);
  const currentSpecialist = SPECIALISTS.find((s) => s.key === activeSpecialist) || SPECIALISTS[0];

  // Filtra as conversas pertencentes ao Agente Geral (múltiplos contextos separados)
  const generalSessions = sessions.filter((s) => {
    return getSessionSpecialist(s) === 'general';
  });

  const handleDeleteGeneralSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await deleteSessionApi({ url: `/chatbot/${sessionId}` });
      if (activeSessionId === sessionId) {
        handleNewChat();
      }
      loadSessions();
    } catch {
      // ignore
    }
  };

  const scrollToBottom = useCallback((instant = false) => {
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' });
      });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && chatbotUnreadCount > 0 && setChatbotUnreadCount) {
      setChatbotUnreadCount(0);
    }
    if (isOpen) {
      const checkbox = document.getElementById('main-base__slide-itens');
      if (checkbox && checkbox.checked) {
        checkbox.checked = false;
      }
    }
  }, [isOpen, chatbotUnreadCount, setChatbotUnreadCount]);

  const adjustTextareaHeight = useCallback(() => {
    const el = inputRef.current;
    if (el) {
      el.style.height = 'auto';
      const scrollHeight = el.scrollHeight;
      const targetHeight = Math.min(Math.max(scrollHeight, 42), 140);
      el.style.height = `${targetHeight}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  useEffect(() => {
    if (isOpen && chatbotInitialPrompt) {
      setInput(chatbotInitialPrompt);
      if (setChatbotInitialPrompt) {
        setChatbotInitialPrompt('');
      }
      setTimeout(() => {
        if (inputRef.current) {
          adjustTextareaHeight();
          inputRef.current.focus();
          inputRef.current.scrollTop = 0;
        }
      }, 150);
    }
  }, [isOpen, chatbotInitialPrompt, setChatbotInitialPrompt, adjustTextareaHeight]);

  const lastTotalMessagesRef = useRef(null);

  const [lastSeenSpecialists, setLastSeenSpecialists] = useState({});

  const findSpecialistSession = useCallback(
    (specKey, sessionList = sessions) => {
      return sessionList.find((s) => getSessionSpecialist(s) === specKey);
    },
    [sessions]
  );

  const loadSessions = useCallback(async () => {
    if (!credencials) return;
    try {
      const res = await callApi({ method: 'get', url: '/chatbot' }, credencials);
      if (res && res.data) {
        const loadedSessions = res.data || [];

        // Sincroniza o mapa de last_read_at recebido do servidor para cada especialista
        const serverSeenMap = {};
        loadedSessions.forEach((sess) => {
          if (sess && sess.last_read_at) {
            const specKey = getSessionSpecialist(sess);
            if (!serverSeenMap[specKey]) {
              serverSeenMap[specKey] = sess.last_read_at;
            }
          }
        });

        if (Object.keys(serverSeenMap).length > 0) {
          setLastSeenSpecialists((prev) => ({
            ...serverSeenMap,
            ...prev,
          }));
        }

        // Conta o total de mensagens de modelo/assistente recebidas em todas as sessões
        let currentTotalMessages = 0;
        loadedSessions.forEach((sess) => {
          if (Array.isArray(sess.messages)) {
            sess.messages.forEach((m) => {
              if (m && (m.role === 'model' || m.role === 'assistant')) {
                currentTotalMessages++;
              }
            });
          }
        });

        // Toca o som de notificação APENAS quando novas mensagens reais do backend chegam
        if (
          lastTotalMessagesRef.current !== null &&
          currentTotalMessages > lastTotalMessagesRef.current
        ) {
          playNotificationSound();
        }
        lastTotalMessagesRef.current = currentTotalMessages;

        setSessions(loadedSessions);
      }
    } catch {
      // ignore
    }
  }, [credencials]);

  useEffect(() => {
    if (!hasAccess) return;
    loadSessions();

    let debounceTimer = null;
    const handleApiActivity = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadSessions();
      }, 600);
    };

    window.addEventListener('duzz:api_request_success', handleApiActivity);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('duzz:api_request_success', handleApiActivity);
    };
  }, [hasAccess, loadSessions]);

  const markSpecialistAsSeen = useCallback(
    (specKey) => {
      const nowIso = new Date().toISOString();
      setLastSeenSpecialists((prev) => ({
        ...prev,
        [specKey]: nowIso,
      }));

      // Notifica o backend para persistência no banco de dados por usuário
      const existing = findSpecialistSession(specKey);
      if (existing && existing.id) {
        markReadApi({ url: `/chatbot/${existing.id}/read`, data: { readAt: nowIso } }).catch(() => { });
      } else {
        markReadApi({ url: '/chatbot/read', data: { specialist: specKey, readAt: nowIso } }).catch(() => { });
      }
    },
    [findSpecialistSession, markReadApi]
  );

  const getSpecialistNotification = useCallback(
    (specKey) => {
      const existing = findSpecialistSession(specKey);
      if (!existing) return { hasUnread: false, hasPendingAction: false, count: 0 };

      let pendingCount = 0;
      let unreadCount = 0;
      const seenVal = lastSeenSpecialists[specKey] || existing.last_read_at;
      const lastSeenTime = seenVal ? new Date(seenVal).getTime() : 0;

      if (Array.isArray(existing.messages)) {
        for (const m of existing.messages) {
          if (m && (m.role === 'assistant' || m.role === 'model')) {
            const msgDate = parseDate(m.timestamp || existing.updated || existing.created);
            const msgTime = msgDate ? msgDate.getTime() : 0;
            if (msgTime > lastSeenTime && activeSpecialist !== specKey) {
              unreadCount++;
              if (
                m.action &&
                m.action.type !== 'copy_shopping_list' &&
                !m.action.executed &&
                !m.action.cancelled
              ) {
                pendingCount++;
              }
            }
          }
        }
      }

      const total = pendingCount > 0 ? pendingCount : unreadCount;
      return {
        hasPendingAction: pendingCount > 0,
        hasUnread: unreadCount > 0,
        count: total,
      };
    },
    [findSpecialistSession, lastSeenSpecialists, activeSpecialist]
  );

  // Sincroniza o contador global do botão DuxIA no menu lateral (SEM tocar som ao trocar abas)
  useEffect(() => {
    if (!setChatbotUnreadCount) return;
    let totalUnread = 0;
    SPECIALISTS.forEach((spec) => {
      const notif = getSpecialistNotification(spec.key);
      if (notif.count > 0 && (!isOpen || activeSpecialist !== spec.key)) {
        totalUnread += notif.count;
      }
    });

    setChatbotUnreadCount(totalUnread);
  }, [sessions, lastSeenSpecialists, activeSpecialist, isOpen, getSpecialistNotification, setChatbotUnreadCount]);

  const handleSelectSpecialist = useCallback(async (specKey) => {
    setActiveSpecialist(specKey);
    markSpecialistAsSeen(specKey);
    if (chatbotSpecialist) {
      setChatbotSpecialist(null);
    }
    const spec = SPECIALISTS.find((s) => s.key === specKey) || SPECIALISTS[0];

    if (specKey === 'general') {
      setActiveSessionId(null);
      setMessages([WELCOME_MESSAGE]);
      setActionResults({});
      setPagination({
        offset: 0,
        limit: 5,
        hasMore: false,
        isLoadingMore: false,
      });
      return;
    }

    const existing = findSpecialistSession(specKey);
    if (existing) {
      await handleLoadSession(existing.id, specKey);
    } else {
      setActiveSessionId(null);
      setMessages([
        {
          role: 'assistant',
          content: spec.defaultWelcome,
          timestamp: new Date().toISOString(),
        },
      ]);
      setActionResults({});
    }
  }, [findSpecialistSession, markSpecialistAsSeen, chatbotSpecialist, setChatbotSpecialist]);

  const handleLoadSession = useCallback(async (sessionId, explicitSpecialistKey) => {
    try {
      const res = await fetchSession({ url: `/chatbot/${sessionId}?limit=5&offset=0` });
      if (res && res.data) {
        const loaded = (res.data.messages || []).map((m) => ({
          role: m.role === 'model' ? 'assistant' : m.role,
          content: m.content,
          data: m.data,
          action: m.action,
          timestamp: m.timestamp || res.data.updated || res.data.created,
          userName: m.userName,
          userId: m.userId,
        }));
        setMessages(loaded.length > 0 ? loaded : [WELCOME_MESSAGE]);
        setActiveSessionId(sessionId);

        // Inicializa o estado de paginação com o próximo lote de 5
        setPagination({
          offset: 5,
          limit: 5,
          hasMore: Boolean(res.data.pagination?.has_more),
          isLoadingMore: false,
        });

        if (explicitSpecialistKey) {
          setActiveSpecialist(explicitSpecialistKey);
        } else {
          setActiveSpecialist(getSessionSpecialist(res.data));
        }

        setActionResults({});

        // Direciona a visualização sempre para o final das mensagens mais recentes
        isAutoScrollingRef.current = true;
        setTimeout(() => {
          scrollToBottom(true);
          setTimeout(() => {
            isAutoScrollingRef.current = false;
          }, 350);
        }, 120);
      }
    } catch {
      // ignore
    }
  }, [fetchSession, scrollToBottom]);

  // Carrega lote anterior de mensagens antigas ao rolar para o topo (Infinite Scroll Reverso)
  const loadOlderMessages = useCallback(async () => {
    if (!activeSessionId || !pagination.hasMore || pagination.isLoadingMore || isAutoScrollingRef.current) {
      return;
    }

    setPagination((prev) => ({ ...prev, isLoadingMore: true }));
    const container = messagesContainerRef.current;
    const prevScrollHeight = container ? container.scrollHeight : 0;
    const prevScrollTop = container ? container.scrollTop : 0;

    try {
      const res = await fetchSession({
        url: `/chatbot/${activeSessionId}?limit=5&offset=${pagination.offset}`,
      });

      if (res && res.data && Array.isArray(res.data.messages)) {
        const olderLoaded = res.data.messages.map((m) => ({
          role: m.role === 'model' ? 'assistant' : m.role,
          content: m.content,
          data: m.data,
          action: m.action,
          timestamp: m.timestamp || res.data.updated || res.data.created,
          userName: m.userName,
          userId: m.userId,
        }));

        if (olderLoaded.length > 0) {
          setMessages((prev) => [...olderLoaded, ...prev]);
          setPagination((prev) => ({
            ...prev,
            offset: prev.offset + 5,
            hasMore: Boolean(res.data.pagination?.has_more),
            isLoadingMore: false,
          }));

          // Preserva a posição visual exata após a inserção das mensagens antigas
          if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(() => {
              if (container) {
                const newScrollHeight = container.scrollHeight;
                container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
              }
            });
          }
        } else {
          setPagination((prev) => ({ ...prev, hasMore: false, isLoadingMore: false }));
        }
      } else {
        setPagination((prev) => ({ ...prev, isLoadingMore: false }));
      }
    } catch {
      setPagination((prev) => ({ ...prev, isLoadingMore: false }));
    }
  }, [activeSessionId, pagination, fetchSession]);

  const handleMessagesScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    if (scrollTop <= 30 && pagination.hasMore && !pagination.isLoadingMore && !isAutoScrollingRef.current) {
      loadOlderMessages();
    }
  };

  // Auto-direcionamento ao abrir o componente da duxIA para a mensagem/notificação não lida mais próxima
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current && sessions.length > 0) {
      let targetSpecKey = null;
      let latestUnreadTime = -1;

      // 1. Prioridade máxima: Especialista com Ação Pendente para decisão imediata
      for (const spec of SPECIALISTS) {
        const notif = getSpecialistNotification(spec.key);
        if (notif.hasPendingAction) {
          targetSpecKey = spec.key;
          break;
        }
      }

      // 2. Segunda prioridade: Especialista com mensagens não lidas mais recentes
      if (!targetSpecKey) {
        for (const spec of SPECIALISTS) {
          const notif = getSpecialistNotification(spec.key);
          if (notif.hasUnread) {
            const sess = findSpecialistSession(spec.key);
            let sessTime = 0;
            if (sess && Array.isArray(sess.messages) && sess.messages.length > 0) {
              const lastMsg = sess.messages[sess.messages.length - 1];
              const d = parseDate(lastMsg.timestamp || sess.updated || sess.created);
              sessTime = d ? d.getTime() : 0;
            }
            if (sessTime > latestUnreadTime) {
              latestUnreadTime = sessTime;
              targetSpecKey = spec.key;
            }
          }
        }
      }

      const specToLoad = chatbotSpecialist || targetSpecKey || activeSpecialist;
      if (chatbotSpecialist) {
        setChatbotSpecialist(null);
      }
      handleSelectSpecialist(specToLoad);
    } else if (isOpen && chatbotSpecialist && chatbotSpecialist !== activeSpecialist) {
      const requestedSpec = chatbotSpecialist;
      setChatbotSpecialist(null);
      handleSelectSpecialist(requestedSpec);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, sessions, getSpecialistNotification, findSpecialistSession, handleSelectSpecialist, activeSpecialist, chatbotSpecialist, setChatbotSpecialist]);

  const handleNewChat = () => {
    const spec = SPECIALISTS.find((s) => s.key === activeSpecialist) || SPECIALISTS[0];
    setActiveSessionId(null);
    setPagination({
      offset: 0,
      limit: 5,
      hasMore: false,
      isLoadingMore: false,
    });
    setMessages([
      {
        role: 'assistant',
        content: spec.defaultWelcome,
        timestamp: new Date().toISOString(),
      },
    ]);
    setActionResults({});
    setMenuAnchorEl(null);
  };

  const handleSendPrompt = (promptText) => {
    setInput(promptText);
    setTimeout(() => {
      handleSendWithText(promptText);
    }, 50);
  };

  const handleSendWithText = async (customText) => {
    const trimmed = (customText !== undefined ? customText : input).trim();
    if (!trimmed || isLoading) return;

    const userMessage = {
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
      userName: credencials?.name || credencials?.username || 'Você',
    };

    // Cancela automaticamente qualquer ação anterior pendente quando o usuário envia nova mensagem
    setMessages((prev) => [
      ...prev.map((m) => {
        if (m.action && !m.action.executed && !m.action.cancelled) {
          return {
            ...m,
            action: {
              ...m.action,
              cancelled: true,
            },
          };
        }
        return m;
      }),
      userMessage,
    ]);
    setInput('');
    setIsLoading(true);

    const history = messages
      .filter((m) => m.role !== 'system')
      .map((m) => ({
        role: m.role === 'assistant' ? 'model' : 'user',
        content: typeof m.content === 'string' ? m.content : m.content,
        timestamp: m.timestamp,
        userName: m.userName,
        userId: m.userId,
      }));

    try {
      const res = await sendMessage({
        data: {
          message: trimmed,
          history,
          sessionId: activeSessionId,
          agent_type: activeSpecialist,
        },
      });

      if (res && res.data) {
        const targetSpec = res.data.target_specialist;
        if (targetSpec && targetSpec !== activeSpecialist && SPECIALISTS.some((s) => s.key === targetSpec)) {
          setActiveSpecialist(targetSpec);
        }

        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: res.data.response,
            data: res.data.data,
            action: res.data.action,
            timestamp: new Date().toISOString(),
          },
        ]);

        playNotificationSound();

        if (res.data.sessionId) {
          setActiveSessionId(res.data.sessionId);
          loadSessions();
        }
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Desculpe, não consegui processar sua solicitação. Tente novamente.',
            timestamp: new Date().toISOString(),
          },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Ocorreu um erro de conexão com a duxIA. Verifique sua rede e tente novamente.',
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsLoading(false);
      setTimeout(() => scrollToBottom(), 80);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendWithText();
    }
  };

  const handleExecuteAction = async (idx, action) => {
    if (!action) {
      setActionResults((prev) => ({
        ...prev,
        [idx]: { cancelled: true },
      }));
      return;
    }

    setExecutingIdx(idx);
    try {
      const res = await executeActionApi({
        data: {
          action_type: action.type,
          params: action.params || action.data || {},
          session_id: activeSessionId,
          message_index: idx,
        },
      });

      if (res && res.data && res.data.success) {
        setActionResults((prev) => ({
          ...prev,
          [idx]: { success: true, message: res.data.message },
        }));
        setMessages((prev) => {
          const updated = prev.map((m, mIdx) => {
            if (mIdx === idx && m.action) {
              return {
                ...m,
                action: {
                  ...m.action,
                  executed: true,
                  result: res.data,
                },
              };
            }
            return m;
          });

          if (res.data.follow_up && res.data.follow_up.response) {
            updated.push({
              role: 'assistant',
              content: res.data.follow_up.response,
              data: res.data.follow_up.data || [],
              action: res.data.follow_up.action || null,
              timestamp: new Date().toISOString(),
            });
          }

          return updated;
        });
        playNotificationSound();
        loadSessions();
        setTimeout(() => scrollToBottom(), 100);
      } else {
        const errMsg = (res && res.data && res.data.error) || 'Erro ao executar ação.';
        setActionResults((prev) => ({
          ...prev,
          [idx]: { error: errMsg },
        }));
      }
    } catch {
      setActionResults((prev) => ({
        ...prev,
        [idx]: { error: 'Falha na conexão com o servidor.' },
      }));
    } finally {
      setExecutingIdx(null);
    }
  };

  const handleCancelAction = async (idx, action) => {
    setActionResults((prev) => ({
      ...prev,
      [idx]: { cancelled: true },
    }));
    setMessages((prev) =>
      prev.map((m, mIdx) => {
        if (mIdx === idx && m.action) {
          return {
            ...m,
            action: {
              ...m.action,
              cancelled: true,
            },
          };
        }
        return m;
      })
    );

    if (activeSessionId) {
      try {
        await cancelActionApi({
          data: {
            session_id: activeSessionId,
            message_index: idx,
          },
        });
        loadSessions();
      } catch {
        // ignore
      }
    }
  };

  const toggleSound = () => {
    const nextVal = !isMuted;
    setIsMuted(nextVal);
    localStorage.setItem('chatbot_sound_muted', String(nextVal));
    setMenuAnchorEl(null);
  };

  const handlePrint = () => {
    setMenuAnchorEl(null);
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const content = messages
      .filter(
        (m) =>
          !m.hidden &&
          !m.isInternal &&
          m.role !== 'system' &&
          !(typeof m.content === 'string' && m.content.trim().startsWith('[SISTEMA'))
      )
      .map(
        (m) =>
          `<div style="margin-bottom:12px;padding:10px;border-radius:8px;background:${m.role === 'user' ? '#eff6ff' : '#f8fafc'
          };border:1px solid #e2e8f0;"><strong>${m.role === 'user' ? 'Você' : currentSpecialist.title
          } (${formatMessageTime(m.timestamp)}):</strong><div style="margin-top:4px;">${marked.parse(
            m.content || ''
          )}</div></div>`
      )
      .join('');
    printWindow.document.write(
      `<html><head><title>Relatório duxIA</title><style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;padding:24px;max-width:800px;margin:auto;color:#1e293b;}</style></head><body><h2>Conversa — ${currentSpecialist.title}</h2><hr style="border:none;border-top:1px solid #e2e8f0;margin-bottom:16px;" />${content}</body></html>`
    );
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const suggestions = SPECIALIST_SUGGESTIONS[activeSpecialist] || SPECIALIST_SUGGESTIONS.general;

  // Verifica se há alguma ação pendente de execução no chat atual (ações de copiar não bloqueiam)
  const hasPendingExecutionAction = messages.some((m, idx) => {
    if (!m || !m.action) return false;
    const { type, executed, cancelled } = m.action;
    const isCopyAction = type === 'copy_shopping_list' || (typeof type === 'string' && type.startsWith('copy'));
    if (isCopyAction) return false;

    const res = actionResults[idx];
    const isDone = Boolean(executed || cancelled || (res && (res.success || res.cancelled)));
    return !isDone;
  });

  const showSuggestions = !hasPendingExecutionAction && !isLoading && Boolean(suggestions && suggestions.length > 0);

  return (
    <>
      {isOpen && (
        <div className="chatbot-panel">
          {/* Header Superior Limpo */}
          <div className="chatbot-header">
            <div className="chatbot-header__left">
              <div className="chatbot-header__avatar">
                {currentSpecialist.key === 'general' ? (
                  <img
                    src="/image/duxia.jpg"
                    alt="duxIA"
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 10,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                ) : (
                  <span>{currentSpecialist.icon}</span>
                )}
              </div>
              <div className="chatbot-header__titles">
                <span className="chatbot-header__name">{currentSpecialist.title}</span>
                <span className="chatbot-header__status">
                  {isLoading ? 'digitando...' : 'online'}
                </span>
              </div>
            </div>

            <div className="chatbot-header__actions">
              {activeSpecialist === 'general' && (messages.length > 1 || activeSessionId !== null) && (
                <button
                  className="chatbot-header__btn-new"
                  onClick={handleNewChat}
                  title="Iniciar nova conversa no Geral"
                >
                  <AddIcon style={{ fontSize: 15 }} />
                  <span>Nova</span>
                </button>
              )}

              <IconButton
                size="small"
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                style={{ color: '#fff' }}
                title="Opções"
              >
                <MoreVertIcon style={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                style={{ color: '#fff' }}
                title="Fechar"
              >
                <CloseIcon style={{ fontSize: 20 }} />
              </IconButton>

              <Menu
                anchorEl={menuAnchorEl}
                keepMounted
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={toggleSound}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    {isMuted ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText primary={isMuted ? 'Ativar som' : 'Silenciar som'} />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMenuAnchorEl(null);
                    setSoundModalOpen(true);
                  }}
                >
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <MusicNoteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Escolher som da notificação" />
                </MenuItem>
                <MenuItem onClick={handlePrint}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <PrintIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Imprimir conversa" />
                </MenuItem>
                <MenuItem onClick={handleNewChat}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <RefreshIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Nova conversa" />
                </MenuItem>
              </Menu>
            </div>
          </div>

          {/* Barra Horizontal de Especialistas (1 Clique) */}
          <div className="chatbot-tabs">
            {SPECIALISTS.map((spec) => {
              const isCurrent = activeSpecialist === spec.key;
              const notif = getSpecialistNotification(spec.key);

              return (
                <button
                  key={spec.key}
                  className={`chatbot-tab ${isCurrent ? 'chatbot-tab--active' : ''} ${notif.count > 0 && !isCurrent ? 'chatbot-tab--has-unread' : ''
                    }`}
                  onClick={() => handleSelectSpecialist(spec.key)}
                  title={spec.subtitle}
                >
                  <span className="chatbot-tab__icon">{spec.icon}</span>
                  <span className="chatbot-tab__label">{spec.shortTitle}</span>
                  {notif.count > 0 && !isCurrent && (
                    <span
                      className={`chatbot-tab__badge-pill ${notif.hasPendingAction ? 'chatbot-tab__badge-pill--action' : ''
                        }`}
                      title={
                        notif.hasPendingAction
                          ? 'Ação pendente aguardando sua resposta'
                          : `${notif.count} nova(s) mensagem(ns)`
                      }
                    >
                      {notif.hasPendingAction ? '!' : notif.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Área de Mensagens */}
          <div
            className="chatbot-messages"
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
          >
            {pagination.isLoadingMore && (
              <div className="chatbot-loading-older">
                <CircularProgress size={14} thickness={5} style={{ color: '#17497f' }} />
                <span>Carregando mensagens anteriores...</span>
              </div>
            )}
            <div className="chatbot-messages__scroll">
              {messages
                .filter(
                  (msg) =>
                    !msg.hidden &&
                    !msg.isInternal &&
                    msg.role !== 'system' &&
                    !(typeof msg.content === 'string' && msg.content.trim().startsWith('[SISTEMA'))
                )
                .map((msg, idx) => (
                  <div
                    key={idx}
                    className={`chatbot-bubble ${msg.role === 'user' ? 'chatbot-bubble--user' : 'chatbot-bubble--bot'
                      }`}
                  >
                    {msg.role === 'user' && msg.userName && (
                      <div className="chatbot-bubble__author">
                        👤 {msg.userName}
                      </div>
                    )}

                    <div
                      className="chatbot-bubble__content"
                      dangerouslySetInnerHTML={{
                        __html: formatMessage(msg.content),
                      }}
                    />

                    {msg.action && (
                      <ActionProposalCard
                        action={msg.action}
                        result={actionResults[idx]}
                        isExecuting={executingIdx === idx}
                        onExecute={(action) => handleExecuteAction(idx, action)}
                        onCancel={(action) => handleCancelAction(idx, action)}
                      />
                    )}

                    {msg.data && msg.data.length > 0 && !msg.action && (
                      <CollapsibleDataTable data={msg.data} />
                    )}

                    <span className="chatbot-bubble__time">
                      {formatMessageTime(msg.timestamp)}
                    </span>
                  </div>
                ))}

              {isLoading && (
                <div className="chatbot-bubble chatbot-bubble--bot chatbot-bubble--loading">
                  <div className="chatbot-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}

              {/* Sugestões Rápidas (Chips de 1 Clique) na tela inicial */}
              {showSuggestions && (
                <div className="chatbot-suggestions">
                  <div className="chatbot-suggestions__title">💡 Sugestões rápidas:</div>
                  <div className="chatbot-suggestions__list">
                    {suggestions.map((sug, sIdx) => (
                      <button
                        key={sIdx}
                        className="chatbot-suggestion-chip"
                        onClick={() => handleSendPrompt(sug.prompt)}
                      >
                        {sug.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Lista de Conversas Anteriores do Geral no espaço abaixo das sugestões */}
              {activeSpecialist === 'general' && messages.length <= 1 && generalSessions.length > 0 && (
                <div className="chatbot-recent-sessions">
                  <div className="chatbot-recent-sessions__title">
                    <HistoryIcon style={{ fontSize: 16 }} />
                    <span>Conversas recentes:</span>
                  </div>
                  <div className="chatbot-recent-sessions__list">
                    {generalSessions.map((s) => {
                      const dateStr = s.updated || s.created;
                      return (
                        <div
                          key={s.id}
                          className="chatbot-recent-sessions__item"
                          onClick={() => handleLoadSession(s.id, 'general')}
                        >
                          <div className="chatbot-recent-sessions__item-left">
                            <div className="chatbot-recent-sessions__item-icon">
                              <ChatBubbleOutlineIcon style={{ fontSize: 15 }} />
                            </div>
                            <div className="chatbot-recent-sessions__item-info">
                              <span className="chatbot-recent-sessions__item-text">
                                {s.title || 'Conversa sem título'}
                              </span>
                              <span className="chatbot-recent-sessions__item-date">
                                {formatMessageTime(dateStr)}
                              </span>
                            </div>
                          </div>
                          <IconButton
                            size="small"
                            className="chatbot-recent-sessions__item-del"
                            onClick={(e) => handleDeleteGeneralSession(e, s.id)}
                            title="Excluir conversa"
                          >
                            <DeleteOutlineIcon style={{ fontSize: 15 }} />
                          </IconButton>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Inferior Limpo e Prático */}
          <div className="chatbot-input">
            <textarea
              ref={inputRef}
              className="chatbot-input__field"
              placeholder="Digite sua dúvida ou peça uma ação..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <IconButton
              className="chatbot-input__send"
              onClick={() => handleSendWithText()}
              disabled={isLoading || !input.trim()}
              size="small"
            >
              {isLoading ? (
                <CircularProgress size={18} style={{ color: '#fff' }} />
              ) : (
                <SendIcon style={{ fontSize: 18, color: '#fff' }} />
              )}
            </IconButton>
          </div>
        </div>
      )}

      {/* Dialog para Ouvir e Escolher o Som da Notificação */}
      <Dialog
        open={soundModalOpen}
        onClose={() => setSoundModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: { borderRadius: 14, padding: '4px' },
        }}
      >
        <DialogTitle style={{ padding: '16px 20px 6px', fontSize: 16, fontWeight: 700, color: '#17497f' }}>
          🎵 Som da Notificação duxIA
        </DialogTitle>
        <DialogContent style={{ padding: '8px 20px' }}>
          <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 12px 0' }}>
            Clique em <strong>▶ Ouvir</strong> para escutar cada som e selecione o que você achar melhor:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SOUND_PRESETS.map((preset) => {
              const isSelected = soundPreset === preset.id;
              return (
                <div
                  key={preset.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: isSelected ? '1.5px solid #17497f' : '1px solid #e2e8f0',
                    backgroundColor: isSelected ? '#eff6ff' : '#f8fafc',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  onClick={() => {
                    setSoundPreset(preset.id);
                    localStorage.setItem('chatbot_sound_preset', preset.id);
                    playSoundById(preset.id);
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: isSelected ? 700 : 600,
                        color: isSelected ? '#17497f' : '#1e293b',
                      }}
                    >
                      {preset.name} {isSelected && '✓'}
                    </span>
                    <span style={{ fontSize: 11, color: '#64748b' }}>{preset.desc}</span>
                  </div>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      playSoundById(preset.id);
                    }}
                    style={{
                      minWidth: 70,
                      fontSize: 11,
                      textTransform: 'none',
                      borderRadius: 6,
                      borderColor: '#cbd5e1',
                      fontWeight: 600,
                    }}
                  >
                    ▶ Ouvir
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px' }}>
          <Button
            onClick={() => setSoundModalOpen(false)}
            color="primary"
            variant="contained"
            style={{
              textTransform: 'none',
              backgroundColor: '#17497f',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Concluído
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Chatbot;
